import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Building2,
  Phone,
  Star,
  Clock,
  Facebook,
  Instagram,
  Twitter,
  AlertCircle,
} from "lucide-react";
import {
  useCreateBusiness,
  type BusinessTranslation,
} from "../../../services/businessService";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import ImageUpload from "../../../components/common/ImageUpload";
import { ACCEPTABLE_LANGUAGES } from "../../../constants/languages";

interface BusinessFormData {
  category: string;
  image?: string;
  gallery?: string[];
  owner: string;
  established?: string;
  location: string;
  contact_phone: string;
  contact_email: string;
  contact_website?: string;
  services?: string[];
  features?: string[];
  hours?: {
    monday?: string;
    tuesday?: string;
    wednesday?: string;
    thursday?: string;
    friday?: string;
    saturday?: string;
    sunday?: string;
  };
  social_media?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };
  rating?: number;
  reviews?: number;
  featured?: boolean;
  verified?: boolean;
  translations: BusinessTranslation[];
}

const BusinessCreate: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const navigate = useNavigate();

  const createBusinessMutation = useCreateBusiness();

  const [formData, setFormData] = useState<BusinessFormData>({
    category: "",
    image: "",
    gallery: [],
    owner: "",
    established: "",
    location: "",
    contact_phone: "",
    contact_email: "",
    contact_website: "",
    services: [],
    features: [],
    hours: {
      monday: "",
      tuesday: "",
      wednesday: "",
      thursday: "",
      friday: "",
      saturday: "",
      sunday: "",
    },
    social_media: {
      facebook: "",
      instagram: "",
      twitter: "",
    },
    rating: 0,
    reviews: 0,
    featured: false,
    verified: false,
    translations: ACCEPTABLE_LANGUAGES.map((lang) => ({
      locale: lang,
      name: "",
      description: "",
    })),
  });

  const [currentService, setCurrentService] = useState("");
  const [currentFeature, setCurrentFeature] = useState("");
  const [validationError, setValidationError] = useState("");

  const handleInputChange = (
    field: keyof BusinessFormData,
    value: string | number | boolean | string[] | object
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTranslationChange = (
    locale: string,
    field: keyof BusinessTranslation,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((translation) =>
        translation.locale === locale
          ? { ...translation, [field]: value }
          : translation
      ),
    }));
  };

  const addService = () => {
    if (currentService.trim()) {
      setFormData((prev) => ({
        ...prev,
        services: [...(prev.services || []), currentService.trim()],
      }));
      setCurrentService("");
    }
  };

  const removeService = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services?.filter((_, i) => i !== index) || [],
    }));
  };

  const addFeature = () => {
    if (currentFeature.trim()) {
      setFormData((prev) => ({
        ...prev,
        features: [...(prev.features || []), currentFeature.trim()],
      }));
      setCurrentFeature("");
    }
  };

  const removeFeature = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features?.filter((_, i) => i !== index) || [],
    }));
  };

  const handleImageUpload = useCallback(
    async (files: File[]): Promise<string[]> => {
      const file = files[0];
      if (!file) return [];

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result as string;
          resolve([result]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    []
  );

  const handleImageChange = useCallback((url: string | string[]) => {
    const imageUrl = Array.isArray(url) ? url[0] || "" : url || "";
    setFormData((prev) => ({ ...prev, image: imageUrl }));
  }, []);

  const handleGalleryUpload = useCallback(
    async (files: File[]): Promise<string[]> => {
      const base64Promises = files.map((file) => {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const result = e.target?.result as string;
            resolve(result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });

      try {
        const base64Strings = await Promise.all(base64Promises);
        setFormData((prev) => ({
          ...prev,
          gallery: [...(prev.gallery || []), ...base64Strings],
        }));
        return base64Strings;
      } catch (error) {
        console.error("Error processing gallery upload:", error);
        throw error;
      }
    },
    []
  );

  const handleGalleryChange = useCallback((urls: string | string[]) => {
    const urlArray = Array.isArray(urls) ? urls : [urls].filter(Boolean);
    setFormData((prev) => ({ ...prev, gallery: urlArray }));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError("");

    // Validate required fields
    if (!formData.category.trim()) {
      setValidationError("Category is required");
      return;
    }

    if (!formData.owner.trim()) {
      setValidationError("Owner is required");
      return;
    }

    if (!formData.location.trim()) {
      setValidationError("Location is required");
      return;
    }

    if (!formData.contact_phone.trim()) {
      setValidationError("Contact phone is required");
      return;
    }

    if (!formData.contact_email.trim()) {
      setValidationError("Contact email is required");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.contact_email)) {
      setValidationError("Invalid email format");
      return;
    }

    // Validate at least one translation has name
    const hasValidTranslation = formData.translations.some(
      (t) => t.name && t.name.trim()
    );
    if (!hasValidTranslation) {
      setValidationError("At least one translation with name is required");
      return;
    }

    try {
      await createBusinessMutation.mutateAsync({
        category: formData.category,
        image: formData.image || undefined,
        gallery:
          formData.gallery && formData.gallery.length > 0
            ? formData.gallery
            : undefined,
        owner: formData.owner,
        established: formData.established || undefined,
        location: formData.location,
        contact_phone: formData.contact_phone,
        contact_email: formData.contact_email,
        contact_website: formData.contact_website || undefined,
        services:
          formData.services && formData.services.length > 0
            ? formData.services
            : undefined,
        features:
          formData.features && formData.features.length > 0
            ? formData.features
            : undefined,
        hours: Object.values(formData.hours || {}).some((h) => h)
          ? formData.hours
          : undefined,
        social_media: Object.values(formData.social_media || {}).some((s) => s)
          ? formData.social_media
          : undefined,
        rating: formData.rating || 0,
        reviews: formData.reviews || 0,
        featured: formData.featured || false,
        verified: formData.verified || false,
        translations: formData.translations.filter(
          (t) => t.name && t.name.trim()
        ),
      });
      navigate(`/${currentLanguage}/admin/businesses`);
    } catch (error: unknown) {
      console.error("Failed to create business:", error);
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { data?: { errors?: Record<string, string[]> } };
        };
        if (axiosError.response?.data?.errors) {
          const errors = axiosError.response.data.errors;
          const firstError = Object.values(errors).flat()[0];
          setValidationError(firstError || "Failed to create business");
        } else {
          setValidationError("Failed to create business");
        }
      } else if (error instanceof Error) {
        setValidationError(error.message || "Failed to create business");
      } else {
        setValidationError("Failed to create business");
      }
    }
  };

  const daysOfWeek = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ] as const;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-6"
        >
          <Button
            variant="outline"
            onClick={() => navigate(`/${currentLanguage}/admin/businesses`)}
            className="flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("common.back")}
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              {t("admin.businesses.createNew", "Create New Business")}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t(
                "admin.businesses.createSubtitle",
                "Add a new business to the directory"
              )}
            </p>
          </div>
        </motion.div>

        {/* Validation Error */}
        {validationError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-600 dark:text-red-400 flex-1">
              {validationError}
            </p>
          </motion.div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700">
                <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
                  <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  {t("admin.businesses.basicInfo", "Basic Information")}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="category">
                      {t("admin.businesses.category", "Category")} *
                    </Label>
                    <Input
                      id="category"
                      value={formData.category}
                      onChange={(e) =>
                        handleInputChange("category", e.target.value)
                      }
                      placeholder={t(
                        "admin.businesses.categoryPlaceholder",
                        "e.g., Restaurant, Shop"
                      )}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="owner">
                      {t("admin.businesses.owner", "Owner")} *
                    </Label>
                    <Input
                      id="owner"
                      value={formData.owner}
                      onChange={(e) =>
                        handleInputChange("owner", e.target.value)
                      }
                      placeholder={t(
                        "admin.businesses.ownerPlaceholder",
                        "Business owner name"
                      )}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="established">
                    {t("admin.businesses.established", "Established")}
                  </Label>
                  <Input
                    id="established"
                    value={formData.established}
                    onChange={(e) =>
                      handleInputChange("established", e.target.value)
                    }
                    placeholder={t(
                      "admin.businesses.establishedPlaceholder",
                      "e.g., 2020"
                    )}
                  />
                </div>

                <div>
                  <Label htmlFor="location">
                    {t("admin.businesses.location", "Location")} *
                  </Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) =>
                      handleInputChange("location", e.target.value)
                    }
                    placeholder={t(
                      "admin.businesses.locationPlaceholder",
                      "Business address"
                    )}
                    required
                  />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700">
                <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
                  <Phone className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  {t("admin.businesses.contactInfo", "Contact Information")}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_phone">
                      {t("admin.businesses.phone", "Phone")} *
                    </Label>
                    <Input
                      id="contact_phone"
                      type="tel"
                      value={formData.contact_phone}
                      onChange={(e) =>
                        handleInputChange("contact_phone", e.target.value)
                      }
                      placeholder={t(
                        "admin.businesses.phonePlaceholder",
                        "+237 123 456 789"
                      )}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_email">
                      {t("admin.businesses.email", "Email")} *
                    </Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) =>
                        handleInputChange("contact_email", e.target.value)
                      }
                      placeholder={t(
                        "admin.businesses.emailPlaceholder",
                        "business@example.com"
                      )}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="contact_website">
                    {t("admin.businesses.website", "Website")}
                  </Label>
                  <Input
                    id="contact_website"
                    type="url"
                    value={formData.contact_website}
                    onChange={(e) =>
                      handleInputChange("contact_website", e.target.value)
                    }
                    placeholder={t(
                      "admin.businesses.websitePlaceholder",
                      "https://example.com"
                    )}
                  />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Images */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 border-b border-gray-200 dark:border-gray-700">
                <CardTitle className="text-lg font-bold text-gray-900 dark:text-white">
                  {t("admin.businesses.mainImage", "Main Image")}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ImageUpload
                  value={formData.image}
                  onChange={handleImageChange}
                  onUpload={handleImageUpload}
                  multiple={false}
                />
              </CardContent>
            </Card>

            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 border-b border-gray-200 dark:border-gray-700">
                <CardTitle className="text-lg font-bold text-gray-900 dark:text-white">
                  {t("admin.businesses.gallery", "Gallery")}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ImageUpload
                  value={formData.gallery}
                  onChange={handleGalleryChange}
                  onUpload={handleGalleryUpload}
                  multiple={true}
                  maxFiles={20}
                />
              </CardContent>
            </Card>
          </motion.div>

          {/* Services & Features */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-b border-gray-200 dark:border-gray-700">
                <CardTitle className="text-lg font-bold text-gray-900 dark:text-white">
                  {t("admin.businesses.services", "Services")}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={currentService}
                    onChange={(e) => setCurrentService(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addService();
                      }
                    }}
                    placeholder={t(
                      "admin.businesses.servicePlaceholder",
                      "Add a service"
                    )}
                  />
                  <Button type="button" onClick={addService}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.services?.map((service, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded"
                    >
                      <span>{service}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeService(index)}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-b border-gray-200 dark:border-gray-700">
                <CardTitle className="text-lg font-bold text-gray-900 dark:text-white">
                  {t("admin.businesses.features", "Features")}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={currentFeature}
                    onChange={(e) => setCurrentFeature(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addFeature();
                      }
                    }}
                    placeholder={t(
                      "admin.businesses.featurePlaceholder",
                      "Add a feature"
                    )}
                  />
                  <Button type="button" onClick={addFeature}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.features?.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded"
                    >
                      <span>{feature}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFeature(index)}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Business Hours */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border-b border-gray-200 dark:border-gray-700">
                <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
                  <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                  {t("admin.businesses.hours", "Business Hours")}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {daysOfWeek.map((day) => (
                    <div key={day}>
                      <Label htmlFor={`hours_${day}`} className="capitalize">
                        {day}
                      </Label>
                      <Input
                        id={`hours_${day}`}
                        value={formData.hours?.[day] || ""}
                        onChange={(e) =>
                          handleInputChange("hours", {
                            ...formData.hours,
                            [day]: e.target.value,
                          })
                        }
                        placeholder={t(
                          "admin.businesses.hoursPlaceholder",
                          "e.g., 9:00 AM - 5:00 PM"
                        )}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Social Media */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 border-b border-gray-200 dark:border-gray-700">
                <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
                  <Facebook className="w-5 h-5 text-pink-600 dark:text-pink-400" />
                  {t("admin.businesses.socialMedia", "Social Media")}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label htmlFor="facebook">
                    <Facebook className="w-4 h-4 inline mr-2" />
                    Facebook
                  </Label>
                  <Input
                    id="facebook"
                    type="url"
                    value={formData.social_media?.facebook || ""}
                    onChange={(e) =>
                      handleInputChange("social_media", {
                        ...formData.social_media,
                        facebook: e.target.value,
                      })
                    }
                    placeholder="https://facebook.com/yourpage"
                  />
                </div>
                <div>
                  <Label htmlFor="instagram">
                    <Instagram className="w-4 h-4 inline mr-2" />
                    Instagram
                  </Label>
                  <Input
                    id="instagram"
                    type="url"
                    value={formData.social_media?.instagram || ""}
                    onChange={(e) =>
                      handleInputChange("social_media", {
                        ...formData.social_media,
                        instagram: e.target.value,
                      })
                    }
                    placeholder="https://instagram.com/yourpage"
                  />
                </div>
                <div>
                  <Label htmlFor="twitter">
                    <Twitter className="w-4 h-4 inline mr-2" />
                    Twitter
                  </Label>
                  <Input
                    id="twitter"
                    type="url"
                    value={formData.social_media?.twitter || ""}
                    onChange={(e) =>
                      handleInputChange("social_media", {
                        ...formData.social_media,
                        twitter: e.target.value,
                      })
                    }
                    placeholder="https://twitter.com/yourpage"
                  />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Rating & Status */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border-b border-gray-200 dark:border-gray-700">
                <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
                  <Star className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                  {t("admin.businesses.ratingStatus", "Rating & Status")}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="rating">
                      {t("admin.businesses.rating", "Rating")}
                    </Label>
                    <Input
                      id="rating"
                      type="number"
                      min="0"
                      max="5"
                      step="0.1"
                      value={formData.rating}
                      onChange={(e) =>
                        handleInputChange(
                          "rating",
                          parseFloat(e.target.value) || 0
                        )
                      }
                    />
                  </div>
                  <div>
                    <Label htmlFor="reviews">
                      {t("admin.businesses.reviews", "Reviews")}
                    </Label>
                    <Input
                      id="reviews"
                      type="number"
                      min="0"
                      value={formData.reviews}
                      onChange={(e) =>
                        handleInputChange(
                          "reviews",
                          parseInt(e.target.value) || 0
                        )
                      }
                    />
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="featured"
                      checked={formData.featured}
                      onChange={(e) =>
                        handleInputChange("featured", e.target.checked)
                      }
                      className="w-4 h-4"
                    />
                    <Label htmlFor="featured">
                      {t("admin.businesses.featured", "Featured")}
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="verified"
                      checked={formData.verified}
                      onChange={(e) =>
                        handleInputChange("verified", e.target.checked)
                      }
                      className="w-4 h-4"
                    />
                    <Label htmlFor="verified">
                      {t("admin.businesses.verified", "Verified")}
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Translations */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700">
                <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">
                  {t("admin.businesses.translations", "Translations")}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                {ACCEPTABLE_LANGUAGES.map((lang) => {
                  const translation = formData.translations.find(
                    (t) => t.locale === lang
                  );
                  return (
                    <div
                      key={lang}
                      className="space-y-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                    >
                      <h3 className="font-semibold text-gray-900 dark:text-white uppercase">
                        {lang}
                      </h3>
                      <div>
                        <Label htmlFor={`name_${lang}`}>
                          {t("admin.businesses.name", "Name")} *
                        </Label>
                        <Input
                          id={`name_${lang}`}
                          value={translation?.name || ""}
                          onChange={(e) =>
                            handleTranslationChange(
                              lang,
                              "name",
                              e.target.value
                            )
                          }
                          placeholder={t(
                            "admin.businesses.namePlaceholder",
                            "Business name"
                          )}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor={`description_${lang}`}>
                          {t("admin.businesses.description", "Description")}
                        </Label>
                        <textarea
                          id={`description_${lang}`}
                          value={translation?.description || ""}
                          onChange={(e) =>
                            handleTranslationChange(
                              lang,
                              "description",
                              e.target.value
                            )
                          }
                          placeholder={t(
                            "admin.businesses.descriptionPlaceholder",
                            "Business description"
                          )}
                          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                          rows={4}
                        />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </motion.div>

          {/* Submit Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="flex justify-end gap-4"
          >
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/${currentLanguage}/admin/businesses`)}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              type="submit"
              disabled={createBusinessMutation.isPending}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              {createBusinessMutation.isPending
                ? t("common.saving", "Saving...")
                : t("admin.businesses.create", "Create Business")}
            </Button>
          </motion.div>
        </form>
      </div>
    </div>
  );
};

export default BusinessCreate;
